/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * TOC dropdown showing each chapter (or section) with its question count.
 *
 * @file QuestionPreviewChapterSelect.jsx
 * @author Anuja Gaikwad
 * @since 3.0.0
 */

import React, { useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Select } from '@mui/material';
import Icon from '../icons/Icon';
import QuestionPreviewChapterSelectIcon from './QuestionPreviewChapterSelectIcon';
import { QuestionPreviewChapterSelectStylesRoot, classes } from './styles/QuestionPreviewStyles';
import { formatQuestionCount } from './questionPreviewUtils';

// Opens the menu below the field (MUI defaults to overlaying it on the selected item).
const MENU_BELOW_FIELD_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' }
};

const renderMenuItemCheckIcon = (isSelected) => {
  if (isSelected) {
    return <Icon name="TickIcon" className={classes.chapterMenuItemCheckIcon} aria-hidden />;
  }

  return (
    <span
      className={`${classes.chapterMenuItemCheckIcon} ${classes.chapterMenuItemCheckIconPlaceholder}`}
      aria-hidden
    />
  );
};

/**
 * TOC dropdown for question previews. Lists each option (a chapter on the preview page, a
 * flattened section in the Questions & Topics modal) with its question count on the right,
 * as in the Figma combobox. Zero-question options are shown but disabled.
 *
 * @param {Array<{ id, title, questionCount }>} chapters options, in display order
 * @param {string} selectedChapterId currently selected option id
 * @param {object} textInfo localized labels (aria label, placeholder, question count)
 * @param {Function} onChange called with the newly selected option id
 * @param {string} label optional visible field label rendered above the select
 * @param {string} variant 'section' applies the Questions & Topics modal styling
 */
const QuestionPreviewChapterSelect = ({
  chapters,
  selectedChapterId,
  textInfo,
  onChange,
  label,
  variant,
  wrapClassName = ''
}) => {
  const labelId = useId();
  const selectRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState();
  const isSectionVariant = variant === 'section';
  const wrapClassNameResolved = [
    classes.chapterSelectWrap,
    isSectionVariant ? classes.sectionSelectWrap : classes.chapterPreviewSelectWrap,
    wrapClassName
  ].filter(Boolean).join(' ');

  const renderChapterLabel = (chapter, valueClassName = classes.chapterSelectValue) => (
    <span className={valueClassName}>
      <span className={classes.chapterSelectTitle}>{chapter.title}</span>
      <span className={classes.chapterSelectCount}>
        {formatQuestionCount(chapter.questionCount, textInfo)}
      </span>
    </span>
  );

  const renderValue = (chapterId) => {
    const chapter = chapters.find((item) => item.id === chapterId);

    return chapter ? renderChapterLabel(chapter) : textInfo.chapterSelectPlaceholder;
  };

  return (
    <QuestionPreviewChapterSelectStylesRoot className={wrapClassNameResolved}>
      {label && (
        <span id={labelId} className={classes.chapterSelectLabel}>{label}</span>
      )}
      <Select
        className={classes.chapterSelect}
        value={selectedChapterId}
        onChange={(event) => onChange(event.target.value)}
        renderValue={renderValue}
        displayEmpty
        IconComponent={QuestionPreviewChapterSelectIcon}
        ref={selectRef}
        onOpen={() => setMenuWidth(selectRef.current?.clientWidth)}
        labelId={label ? labelId : undefined}
        inputProps={label ? {} : { 'aria-label': textInfo.chapterSelectLabel }}
        MenuProps={{
          // Keeps the menu inside the scoped style root; a portal would drop the aet-question-preview-* rules.
          disablePortal: true,
          classes: { paper: classes.chapterMenuPaper },
          ...(isSectionVariant
            ? {
              ...MENU_BELOW_FIELD_PROPS,
              slotProps: { paper: { style: { width: menuWidth } } }
            }
            : {
              ...MENU_BELOW_FIELD_PROPS,
              slotProps: {
                paper: {
                  style: {
                    width: menuWidth,
                    minWidth: menuWidth,
                    maxWidth: menuWidth
                  }
                }
              }
            })
        }}
      >
        {chapters.map((chapter) => {
          const isDisabled = chapter.questionCount === 0;
          const isSelected = chapter.id === selectedChapterId;

          return (
            <MenuItem
              key={chapter.id}
              value={chapter.id}
              disabled={isDisabled}
              className={classes.chapterMenuItem}
            >
              <span className={classes.chapterMenuItemRow}>
                {renderChapterLabel(chapter, classes.chapterMenuItemValue)}
                {renderMenuItemCheckIcon(isSelected)}
              </span>
            </MenuItem>
          );
        })}
      </Select>
    </QuestionPreviewChapterSelectStylesRoot>
  );
};

export const chapterOptionPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  questionCount: PropTypes.number.isRequired
});

QuestionPreviewChapterSelect.propTypes = {
  chapters: PropTypes.arrayOf(chapterOptionPropType).isRequired,
  selectedChapterId: PropTypes.string,
  textInfo: PropTypes.shape({
    chapterSelectLabel: PropTypes.string,
    chapterSelectPlaceholder: PropTypes.string,
    questionLabel: PropTypes.string,
    questionsLabel: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['chapter', 'section']),
  wrapClassName: PropTypes.string
};

QuestionPreviewChapterSelect.defaultProps = {
  selectedChapterId: '',
  label: '',
  variant: 'chapter',
  wrapClassName: ''
};

export default QuestionPreviewChapterSelect;
